import React, { useEffect, useState } from "react";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import { ErrorMessage, Field, Fieldset, Label } from "../../../design/fieldset";
import { useCustodyOrder } from "../../../contexts/CustodyOrderContext";
import {
  CustodyOrder,
  CustodyOrderType,
  custodyOrderTypes,
} from "../../../models/CustodyOrder";
import { Textarea } from "../../../design/textarea";
import { toLocalDateString } from "../../../utils/timeString";
import { Select } from "../../../design/select";

interface Props {
  custodyOrder: CustodyOrder;
}

interface Errors {
  title?: string;
  description?: string;
  type?: string;
  orderDate?: string;
}

const EditCustodyOrderButton: React.FC<Props> = ({ custodyOrder }) => {
  const { updateCustodyOrder } = useCustodyOrder();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState<string>(custodyOrder.title ?? "");
  const [type, setType] = useState<CustodyOrderType>(custodyOrder.type ?? "");
  const [orderDate, setDueDate] = useState<string>(
    toLocalDateString(custodyOrder.orderDate ?? new Date()).slice(0, 10),
  );
  const [description, setDescription] = useState<string>(
    custodyOrder.description ?? "",
  );
  const [errors, setErrors] = useState<Errors>({});

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!title.trim()) {
      newErrors.title = "A title is required.";
    }
    if (!type) {
      newErrors.type = "A type is required.";
    }
    if (!orderDate) {
      newErrors.orderDate = "A due date is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);
    setError(null);

    try {
      await updateCustodyOrder(custodyOrder._id, {
        title,
        type,
        orderDate: new Date(`${orderDate}T00:00:00Z`), // Ensure the date is in ISO format
        description,
      });
      clearForm();
    } catch (e) {
      console.error(e);
      setError("Failed to update custodyOrder. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setDescription(custodyOrder.description ?? "");
    setIsOpen(false);
    setError(null);
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Edit
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Edit Custody Order</DialogTitle>
        <DialogBody>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <Fieldset>
            <Field>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={isSaving}
              />
              {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
            </Field>

            <Field>
              <Label>Type</Label>
              <Select
                value={type}
                onChange={(e) => setType(e.target.value as CustodyOrderType)}
              >
                <option value="" disabled>
                  Select type
                </option>
                {Object.entries(custodyOrderTypes).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.label}
                  </option>
                ))}
              </Select>
              {errors.type && <ErrorMessage>{errors.type}</ErrorMessage>}
            </Field>
            <Field>
              <Label htmlFor="orderDate">Order Date</Label>
              <Input
                id="orderDate"
                type="date"
                value={orderDate}
                onChange={(e) => setDueDate(e.target.value)}
                disabled={isSaving}
              />
              {errors.orderDate && (
                <ErrorMessage>{errors.orderDate}</ErrorMessage>
              )}
            </Field>

            <Field>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                placeholder="What happened?"
                onChange={(e) => setDescription(e.target.value)}
                disabled={isSaving}
              />
            </Field>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditCustodyOrderButton;
